import { MouseEventHandler, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import DeleteConfirmSalesReturn from "../components/DeleteConfirmationSalesReturn"
import Modal from "../components/Modal"
import { FormType, FormTypeList } from "../config"
import { getDate, LocalFormatDate } from "../config/functions"
import { AccountHolderType } from "../redux/actions/accountHolder"
import { AuthUserType, RoleType } from "../redux/actions/authUserActions"
import { CustomerType } from "../redux/actions/customerAction"
import { SalesReturnType, fetchSalesReturnWithSaleInvByInvDateAction } from "../redux/actions/salesReturnAction"
import { AppDispatch, StoreState } from "../redux/store"
import ReactDatePicker from "react-datepicker"
import { FormInput } from "../components/custom/components"

interface TableBody {
    sno: number
    invoiceDate?: string
    saleInvoiceNo: string
    invoiceNo: string
    salesReturnEntryNo: string
    customer: string
    finalAmount: string
    onEdit: MouseEventHandler<HTMLButtonElement>
    onDelete: MouseEventHandler<HTMLButtonElement>
    rolesAccessObj: RoleType | undefined
}

type HandleEdit = (value: SalesReturnType) => void
type HandleDelete = (value: SalesReturnType) => void

interface Props {
    setFormType: React.Dispatch<React.SetStateAction<FormType>>
    setId: React.Dispatch<React.SetStateAction<number | undefined>>
    onclose: () => void
    onEdit: (id: number) => void
    /** When the view modal opens, refetch sales returns for the selected date */
    viewOpen?: boolean
}

const toApiInvoiceDate = (date: Date | null): string =>
    getDate(date ?? new Date())

const SalesReturn = (props: Props) => {
    const dispatch = useDispatch<AppDispatch>()
    const salesReturnList = useSelector<StoreState, SalesReturnType[]>(state => state.salesReturn)
    const AccountLists = useSelector<StoreState, AccountHolderType[]>(state => state.account)
    const CustomerList = useSelector<StoreState, CustomerType[]>(state => state.customer)

    const [showDeleteForm, setShowDeleteForm] = useState<boolean>(false)
    const [editData, setEditData] = useState<SalesReturnType>()
    const [searchDate, setSearchDate] = useState<Date | null>(new Date())
    const [searchCustomer, setSearchCustomer] = useState<string>("")
    const [searchInvoiceNo, setSearchInvoiceNo] = useState<string>("")

    const AuthUser = useSelector<StoreState, AuthUserType | null>(state => state.authUser)
    const url = window.location.pathname
    const rolesAccessObj = AuthUser?.roles?.find(f => f.pageUrl === url)

    const loadSalesReturnForDate = (date: Date | null) => {
        dispatch(fetchSalesReturnWithSaleInvByInvDateAction(toApiInvoiceDate(date)))
    }

    useEffect(() => {
        if (props.viewOpen !== false) {
            loadSalesReturnForDate(searchDate)
        }
    }, [props.viewOpen, dispatch])

    const filteredSalesReturnList = salesReturnList?.filter((sr) => {
        const invoiceNoMatch = searchInvoiceNo
            ? sr.invoiceNumber?.toLowerCase().includes(searchInvoiceNo.toLowerCase()) ||
              sr.salesInvoiceNo?.toLowerCase().includes(searchInvoiceNo.toLowerCase())
            : true

        const customerObj = CustomerList.find(
            (s) => s?.accountNumber?.toString() === sr?.customerId?.toString()
        )
        const customerName = (
            customerObj?.accountName ||
            AccountLists.find((s) => s?.accountNumber === sr?.customerId)?.accountName ||
            ""
        ).toLowerCase()
        const customerMatch = searchCustomer
            ? customerName.includes(searchCustomer.toLowerCase())
            : true

        return invoiceNoMatch && customerMatch
    })

    const handleDateFilter = (date: Date | null) => {
        setSearchDate(date)
        loadSalesReturnForDate(date)
    }

    const handleCustomerFilter = (value: string) => {
        setSearchCustomer(value)
    }

    const handleInvoiceNoFilter = (value: string) => {
        setSearchInvoiceNo(value)
    }

    const handleEdit: HandleEdit = (value) => {
        if (!value.id) {
            console.error("Sales Return ID is missing", value)
            return
        }

        props.onEdit(value.id)
        setEditData(value)
        props.onclose()
    }

    const handleDelete: HandleDelete = (value) => {
        props.setFormType(FormTypeList.DELETE)
        setEditData(value)
        setShowDeleteForm(true)
    }

    const handleDeleteFormClose = () => {
        setShowDeleteForm(false)
        props.setFormType(FormTypeList.ADD)
        loadSalesReturnForDate(searchDate)
    }

    return <>
        <Modal
            headerText={'Delete SalesReturn'}
            visible={showDeleteForm}
            onClose={handleDeleteFormClose}
            centered
            size='sm'
        >
            <DeleteConfirmSalesReturn
                editData={editData}
                onClose={handleDeleteFormClose}
            />
        </Modal>
        <div className="row">
            <div className="col-lg-12">
                <div className="hstack gap-4 mb-3">
                    <div>
                        <label htmlFor="searchDate">Bill Date :</label>
                        <ReactDatePicker
                            id="searchDate"
                            selected={searchDate}
                            onChange={(date) => handleDateFilter(date)}
                            dateFormat="dd-MM-yyyy"
                        />
                    </div>
                    <FormInput
                        name='searchInvoiceNo'
                        label='Bill No :'
                        value={searchInvoiceNo}
                        onChange={(e) => handleInvoiceNoFilter(e.target.value)}
                    />
                    <FormInput
                        name='searchCustomer'
                        label='Customer :'
                        value={searchCustomer}
                        onChange={(e) => handleCustomerFilter(e.target.value)}
                    />
                </div>
            </div>
        </div>
        <div className="row">
            <div className="col-lg-12">
                <div className="card shadow-lg">
                    <div className='table-wrapper'>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th className='text-truncate align-middle'>S.No</th>
                                    <th className='text-truncate align-middle'>Sales Return EntryNo</th>
                                    <th className='text-truncate align-middle'>Bill Date</th>
                                    <th className='text-truncate align-middle'>Sales Bill No</th>
                                    <th className='text-truncate align-middle'>Return Bill No</th>
                                    <th className='text-truncate align-middle'>Customer</th>
                                    <th className='text-truncate align-middle'>Grand Total</th>
                                    <th className='text-truncate align-middle'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSalesReturnList?.map((tr, i) => {
                                    const sobj = CustomerList?.find(s => s?.accountNumber?.toString() === tr?.customerId?.toString())
                                    const cname = sobj?.accountName ?? ''
                                    return <UserBody
                                        sno={i + 1}
                                        invoiceNo={tr.invoiceNumber?.toString() ?? ''}
                                        saleInvoiceNo={tr.salesInvoiceNo?.toString() ?? ''}
                                        invoiceDate={tr.invoiceDate?.toString()}
                                        salesReturnEntryNo={tr.salesReturnEntryNumber?.toString() ?? ''}
                                        finalAmount={tr.grandTotal ? tr.grandTotal?.toString() : '0'}
                                        customer={cname}
                                        onEdit={() => handleEdit(tr)}
                                        onDelete={() => handleDelete(tr)}
                                        rolesAccessObj={rolesAccessObj}
                                        key={tr.id ?? i}
                                    />
                                })}
                                {filteredSalesReturnList?.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-4">No records found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </>
}

const UserBody = ({ sno, invoiceDate, finalAmount, invoiceNo, saleInvoiceNo, salesReturnEntryNo, customer, onEdit, onDelete, rolesAccessObj }: TableBody) => {
    return <tr>
        <td className='align-middle'>{sno}</td>
        <td className='text-capitalize align-middle'>{salesReturnEntryNo}</td>
        <td className='text-capitalize align-middle'>{LocalFormatDate(invoiceDate ? invoiceDate : '')}</td>
        <td className='text-capitalize align-middle'>{saleInvoiceNo}</td>
        <td className='text-capitalize align-middle'>{invoiceNo}</td>
        <td className='text-capitalize align-middle'>{customer}</td>
        <td className='text-capitalize align-middle'>{finalAmount}</td>
        <td className="align-middle">
            {/* <div className='hstack gap-1'>
                {(AuthUser?.role === 'SUPER' && <button className='btn btn-sm fw-bold btn-info p-1' onClick={onEdit}><i className="fe-edit noti-icon"></i></button>)}
                {(AuthUser?.role === 'SUPER' && <button className='btn btn-sm fw-bold btn-danger p-1' onClick={onDelete}><i className="fe-trash-2 noti-icon"></i></button>)}
            </div> */}
            <div className="hstack gap-1">
                    {(rolesAccessObj?.edit || rolesAccessObj?.delete) &&
                        <div className="hstack gap-1">
                            {rolesAccessObj?.edit && <button className="btn btn-sm fw-bold btn-info p-1" onClick={onEdit}>
                                <i className="fe-edit noti-icon"></i>
                            </button>}
                            {rolesAccessObj?.delete && <button className="btn btn-sm fw-bold btn-danger p-1" onClick={onDelete}>
                                <i className="fe-trash-2 noti-icon"></i>
                            </button>}
                        </div>
                    }
            </div>
        </td>
    </tr>
}
export default SalesReturn
